import { useCallback, useEffect, useRef, useState } from 'react';
import { animate, motion, useMotionValue } from 'framer-motion';

function useNonPassiveTouchMove(ref: React.RefObject<HTMLElement | null>, handler: (e: TouchEvent) => void) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener('touchmove', handler, { passive: false });
    return () => el.removeEventListener('touchmove', handler);
  }, [ref, handler]);
}

type Props = {
  src: string;
  alt: string;
  slideKey: string;
  onZoomChange?: (zoomed: boolean) => void;
  /** width — на всю ширину контейнера (технички); contain — вписать в экран (галерея). */
  sizeMode?: 'contain' | 'width';
};

function touchDistance(a: { clientX: number; clientY: number }, b: { clientX: number; clientY: number }): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

/** Pinch-to-zoom в лайтбоксе; при отпускании — плавный возврат к 100%. */
export function ProductLightboxPinchImage({
  src,
  alt,
  slideKey,
  onZoomChange,
  sizeMode = 'contain',
}: Props) {
  const fillWidth = sizeMode === 'width';
  const rootRef = useRef<HTMLDivElement>(null);
  const scaleMv = useMotionValue(1);
  const xMv = useMotionValue(0);
  const yMv = useMotionValue(0);
  const pinchRef = useRef<{
    dist: number;
    scale: number;
    midX: number;
    midY: number;
    panX: number;
    panY: number;
  } | null>(null);
  const [zoomed, setZoomed] = useState(false);

  const snapReset = useCallback(() => {
    animate(scaleMv, 1, { type: 'spring', stiffness: 380, damping: 32 });
    animate(xMv, 0, { type: 'spring', stiffness: 380, damping: 32 });
    animate(yMv, 0, { type: 'spring', stiffness: 380, damping: 32 });
    setZoomed(false);
    onZoomChange?.(false);
  }, [scaleMv, xMv, yMv, onZoomChange]);

  useEffect(() => {
    snapReset();
  }, [slideKey, src, snapReset]);

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.stopPropagation();
      const d = touchDistance(e.touches[0], e.touches[1]);
      pinchRef.current = {
        dist: d,
        scale: scaleMv.get(),
        midX: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        midY: (e.touches[0].clientY + e.touches[1].clientY) / 2,
        panX: xMv.get(),
        panY: yMv.get(),
      };
    }
  };

  const onTouchMoveNative = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const d = touchDistance(e.touches[0], e.touches[1]);
        const next = Math.min(4, Math.max(1, pinchRef.current.scale * (d / pinchRef.current.dist)));
        scaleMv.set(next);
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        if (next > 1.02) {
          xMv.set(pinchRef.current.panX + (midX - pinchRef.current.midX));
          yMv.set(pinchRef.current.panY + (midY - pinchRef.current.midY));
        }
        const z = next > 1.05;
        if (z !== zoomed) {
          setZoomed(z);
          onZoomChange?.(z);
        }
      }
    },
    [scaleMv, xMv, yMv, zoomed, onZoomChange],
  );

  useNonPassiveTouchMove(rootRef, onTouchMoveNative);

  const onTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      pinchRef.current = null;
      if (scaleMv.get() > 1.02) {
        snapReset();
      }
    }
  };

  return (
    <motion.div
      ref={rootRef}
      className={
        fillWidth
          ? 'w-full touch-none'
          : 'flex max-w-full max-h-full items-center justify-center touch-none'
      }
      onClick={(e) => e.stopPropagation()}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <motion.img
        src={src}
        alt={alt}
        referrerPolicy="no-referrer"
        draggable={false}
        className={
          fillWidth
            ? 'block w-full h-auto max-w-none select-none'
            : 'max-w-full max-h-full object-contain select-none'
        }
        style={{
          scale: scaleMv,
          x: xMv,
          y: yMv,
          transformOrigin: 'center center',
        }}
      />
    </motion.div>
  );
}
